import type { DeviceStatus, SiteStatus, SyncStatus, TenantStatus } from '@iot-dashboard/shared';
import { STATUS_LABELS } from '@iot-dashboard/shared';

export type AnyStatus = DeviceStatus | SiteStatus | TenantStatus | SyncStatus;

type Tone = 'good' | 'warning' | 'serious' | 'critical' | 'neutral';

const TONE: Record<AnyStatus, Tone> = {
  online: 'good',
  active: 'good',
  ok: 'good',
  alerting: 'warning',
  running: 'warning',
  offline: 'critical',
  error: 'critical',
  suspended: 'critical',
  dormant: 'neutral',
  unknown: 'neutral',
  never: 'neutral',
};

const LABEL: Record<AnyStatus, string> = {
  ...STATUS_LABELS,
  active: 'Active',
  suspended: 'Suspended',
  ok: 'OK',
  error: 'Error',
  running: 'Running',
  never: 'Never',
  unknown: 'Unknown',
};

export function toneFor(status: AnyStatus): Tone {
  return TONE[status] ?? 'neutral';
}

/** Coloured dot plus a text label – status is never conveyed by colour alone. */
export function StatusDot({ status, label }: { status: AnyStatus; label?: string | false }) {
  const text = label === false ? null : (label ?? LABEL[status] ?? status);
  return (
    <span className="status">
      <span className={`dot dot--${toneFor(status)}`} aria-hidden="true" />
      {text ? <span>{text}</span> : <span className="sr-only">{LABEL[status] ?? status}</span>}
    </span>
  );
}

export function StatusPill({ status, label }: { status: AnyStatus; label?: string }) {
  return (
    <span className="pill">
      <span className={`dot dot--${toneFor(status)}`} aria-hidden="true" />
      {label ?? LABEL[status] ?? status}
    </span>
  );
}
